import React, { useState, useEffect } from 'react';
import './App.css';
import CsvViewer from './components/CsvViewer';
import UserDailyLogViewer from './components/UserDailyLogViewer';
import BusinessDailyLogViewer from './components/BusinessDailyLogViewer';
import StaffAttendanceLogViewer from './components/StaffAttendanceLogViewer';
import UserReportViewer from './components/UserReportViewer';
import UserManagement from './components/UserManagement';

import Login from './components/Login';
import authService from './services/authService';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedLogType, setSelectedLogType] = useState('user-daily');
  const [showUserManagement, setShowUserManagement] = useState(false);

  useEffect(() => {
    // ページ読み込み時にログイン状態を確認
    if (authService.isLoggedIn()) {
      setIsLoggedIn(true);
      setCurrentUser(authService.getCurrentUser());
    }
  }, []);

  const handleLogin = (user) => {
    setIsLoggedIn(true);
    setCurrentUser(user);
  };

  const handleLogout = () => {
    authService.logout();
    setIsLoggedIn(false);
    setCurrentUser(null);
  };

  const logTypes = [
    { id: 'user-daily', name: 'ケース記録', component: UserDailyLogViewer, url: '/data/attendance_data.csv' },
    { id: 'user-report', name: '利用者日報', component: UserReportViewer, url: '/data/report_data.csv' },
    { id: 'business-daily', name: '業務日報ログ', component: BusinessDailyLogViewer, parentUrl: '/data/business-daily.csv', childUrl: '/data/interview-schedule.csv', visitExperienceUrl: '/data/visit-experience.csv', outingScheduleUrl: '/data/outing-schedule.csv' },
    { id: 'staff-attendance', name: 'スタッフ勤怠ログ', component: StaffAttendanceLogViewer, url: '/data/staff-attendance.csv' }
  ];

  // ユーザー管理画面を表示するかどうかを判定
  const shouldShowUserManagement = () => {
    return showUserManagement && authService.isAdmin();
  };

  const getCurrentLogType = () => {
    return logTypes.find(type => type.id === selectedLogType) || logTypes[0];
  };

  const renderCurrentViewer = () => {
    // ユーザー管理画面が表示されている場合
    if (shouldShowUserManagement()) {
      return <UserManagement />;
    }

    const currentType = getCurrentLogType();
    const ViewerComponent = currentType.component;
    
    // 業務日報ログの場合は親データと子データの両方を渡す
    if (currentType.id === 'business-daily') {
      return <ViewerComponent 
        parentCsvUrl={currentType.parentUrl} 
        childCsvUrl={currentType.childUrl}
        visitExperienceCsvUrl={currentType.visitExperienceUrl}
        outingScheduleCsvUrl={currentType.outingScheduleUrl}
      />;
    }
    
    // その他のログタイプは従来通り
    return <ViewerComponent csvUrl={currentType.url} />;
  };

  if (!isLoggedIn) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <div className="header-left">
            <h1>Marlins Data Viewer</h1>
            <p>旧Marlinsシステムでの記録ビューアーです</p>
          </div>
          <div className="header-right">
            <div className="user-info">
              <span>ようこそ、{currentUser?.name}さん</span>
              <button onClick={handleLogout} className="logout-button">
                ログアウト
              </button>
            </div>
          </div>
        </div>
        <div className="log-type-selector">
          <h3>ログタイプを選択してください:</h3>
          <div className="log-type-buttons">
            {logTypes.map(type => (
              <button
                key={type.id}
                className={`log-type-button ${selectedLogType === type.id && !showUserManagement ? 'active' : ''}`}
                onClick={() => {
                  setSelectedLogType(type.id);
                  setShowUserManagement(false);
                }}
              >
                {type.name}
              </button>
            ))}
            {/* 管理者のみユーザー管理ボタンを表示 */}
            {authService.isAdmin() && (
              <button
                className={`log-type-button ${showUserManagement ? 'active' : ''}`}
                onClick={() => {
                  setShowUserManagement(true);
                  setSelectedLogType(null);
                }}
              >
                👥 ユーザー管理
              </button>
            )}
          </div>
        </div>
      </header>
      <main>
        {renderCurrentViewer()}
      </main>
      
      {/* CSVファイルダウンロードセクション */}
      <div className="csv-download-section">
        <h3>📥 CSVファイルダウンロード</h3>
        <div className="download-links">
          <div className="download-group">
            <h4>ケース記録</h4>
            <a href="/data/attendance_data.csv" download className="download-link">
              📊 ケース記録データ (attendance_data.csv)
            </a>
          </div>
          
          <div className="download-group">
            <h4>利用者日報</h4>
            <a href="/data/report_data.csv" download className="download-link">
              📋 利用者日報データ (report_data.csv)
            </a>
          </div>
          
          <div className="download-group">
            <h4>業務日報ログ</h4>
            <a href="/data/business-daily.csv" download className="download-link">
              📝 業務日報データ (business-daily.csv)
            </a>
            <a href="/data/interview-schedule.csv" download className="download-link">
              📅 面談予定データ (interview-schedule.csv)
            </a>
            <a href="/data/visit-experience.csv" download className="download-link">
              🏠 訪問体験データ (visit-experience.csv)
            </a>
            <a href="/data/outing-schedule.csv" download className="download-link">
              🚶 外出予定データ (outing-schedule.csv)
            </a>
          </div>
          
          <div className="download-group">
            <h4>スタッフ勤怠ログ</h4>
            <a href="/data/staff-attendance.csv" download className="download-link">
              👥 スタッフ勤怠データ (staff-attendance.csv)
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App; 